import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Translation, TranslocoLoader, TranslocoLoaderData } from '@jsverse/transloco';
import { Observable, catchError, forkJoin, map, of, shareReplay } from 'rxjs';
import { baseLanguage, segmentFromLocale } from './segment-locale';

type Catalog = Record<string, unknown>;
type SegmentOverlay = { root?: Catalog; scopes?: Record<string, Catalog> };

function isCatalog(value: unknown): value is Catalog {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Keep the localized base tree intact; only curated segment keys replace leaf copy. */
export function mergeCatalog(base: Catalog, overlay: Catalog): Catalog {
  const result: Catalog = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    result[key] = isCatalog(value) && isCatalog(base[key])
      ? mergeCatalog(base[key], value)
      : value;
  }
  return result;
}

/** Loads the base language catalog for the root or a lazy scope and applies a sparse overlay. */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly overlays = new Map<string, Observable<SegmentOverlay>>();

  getTranslation(locale: string, data?: TranslocoLoaderData): Observable<Translation> {
    const { scope, lang, segment } = this.resolvePathParts(locale, data);
    const basePath = scope ? `assets/i18n/${scope}/${lang}.json` : `assets/i18n/${lang}.json`;
    const base$ = this.http.get<Translation>(basePath);
    if (segment === 'GENERAL') return base$;

    return forkJoin({ base: base$, overlay: this.overlay(lang, segment) }).pipe(
      map(({ base, overlay }) => {
        const selected = scope ? overlay.scopes?.[scope] : overlay.root;
        return selected ? mergeCatalog(base, selected) as Translation : base;
      }),
    );
  }

  private overlay(lang: string, segment: string): Observable<SegmentOverlay> {
    const key = `${lang}_${segment}`;
    let cached = this.overlays.get(key);
    if (!cached) {
      cached = this.http.get<SegmentOverlay>(`assets/i18n/segments/${segment}/${lang}.json`).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError(() => {
          // A failed optional overlay must not discard the localized base or poison future retries.
          this.overlays.delete(key);
          return of({});
        }),
      );
      this.overlays.set(key, cached);
    }
    return cached;
  }

  private resolvePathParts(locale: string, data?: TranslocoLoaderData): {
    scope: string | null; lang: string; segment: string;
  } {
    const scopeFromData = this.scopeName(data?.scope);
    const [scopeFromLang, scopedLocale] = locale.includes('/') ? locale.split('/', 2) : [null, locale];
    return {
      scope: scopeFromData ?? scopeFromLang,
      lang: baseLanguage(scopedLocale),
      segment: segmentFromLocale(scopedLocale),
    };
  }

  private scopeName(scope: unknown): string | null {
    if (!scope) return null;
    if (typeof scope === 'string') return scope;
    if (typeof scope === 'object' && 'scope' in scope) {
      const nested = (scope as { scope?: unknown }).scope;
      return typeof nested === 'string' ? nested : null;
    }
    return null;
  }
}
